import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dashboardApi, LeadDashboardResponse, LeadRow, LeadRecentResponse } from '../services/dashboardApi';
import { Download, Users, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight, Filter, Lock, Calendar, AlertTriangle, ShieldCheck } from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import LeadHygieneView from '../components/leads/LeadHygieneView';

const SUPPLIER_LABELS: Record<string, string> = {
    nl: 'NordicLeads',
    lp: 'LunaPark',
    eg: 'eGentic',
    lo: 'LeadOne',
};

const GEO_LABELS: Record<string, string> = {
    se: '🇸🇪 Sweden',
    no: '🇳🇴 Norway',
    fi: '🇫🇮 Finland',
    de: '🇩🇪 Germany',
};

const GEO_FLAGS: Record<string, string> = { se: '🇸🇪', no: '🇳🇴', fi: '🇫🇮', de: '🇩🇪' };

const STATUS_COLORS: Record<string, string> = {
    accepted: 'bg-emerald-100 text-emerald-800',
    rejected: 'bg-red-100 text-red-800',
};

type PeriodPreset = 'current_month' | 'last_month' | 'last_3_months' | 'custom';

const PERIOD_LABELS: Record<PeriodPreset, string> = {
    current_month: 'Current Month',
    last_month: 'Last Month',
    last_3_months: 'Last 3 Months',
    custom: 'Custom',
};

function computeDateRange(preset: PeriodPreset): { start: string; end: string } {
    const now = new Date();
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    switch (preset) {
        case 'current_month':
            return { start: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), end: fmt(now) };
        case 'last_month': {
            const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const e = new Date(now.getFullYear(), now.getMonth(), 0); // last day of prev month
            return { start: fmt(s), end: fmt(e) };
        }
        case 'last_3_months': {
            const s = new Date(now.getFullYear(), now.getMonth() - 2, 1); // 1st of 2 months ago
            return { start: fmt(s), end: fmt(now) };
        }
        default:
            return { start: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), end: fmt(now) };
    }
}

export default function LeadIntakeDashboard() {
    const location = useLocation();
    const navigate = useNavigate();
    const isHygieneRoute = location.pathname.includes('/hygiene');
    const [activeTab, setActiveTab] = useState<'intake' | 'hygiene'>(isHygieneRoute ? 'hygiene' : 'intake');

    useEffect(() => {
        if (location.pathname.includes('/hygiene')) {
            setActiveTab('hygiene');
        } else {
            setActiveTab('intake');
        }
    }, [location.pathname]);

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<LeadDashboardResponse | null>(null);
    const [error, setError] = useState('');

    // Period controls
    const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('current_month');
    const initialRange = computeDateRange('current_month');
    const [customStart, setCustomStart] = useState(initialRange.start);
    const [customEnd, setCustomEnd] = useState(initialRange.end);
    const [geoFilter, setGeoFilter] = useState('');

    // Derived dates — computed from preset or custom inputs
    const { startDate, endDate } = useMemo(() => {
        if (periodPreset === 'custom') return { startDate: customStart, endDate: customEnd };
        const r = computeDateRange(periodPreset);
        return { startDate: r.start, endDate: r.end };
    }, [periodPreset, customStart, customEnd]);

    const handlePresetChange = (preset: PeriodPreset) => {
        setPeriodPreset(preset);
        if (preset !== 'custom') {
            const r = computeDateRange(preset);
            setCustomStart(r.start);
            setCustomEnd(r.end);
        }
    };

    // Paginated table
    const [recentPage, setRecentPage] = useState(1);
    const [recentData, setRecentData] = useState<LeadRecentResponse | null>(null);
    const [tableGeo, setTableGeo] = useState('');
    const [tableSupplier, setTableSupplier] = useState('');
    const [tableStatus, setTableStatus] = useState('');
    const [tableLoading, setTableLoading] = useState(false);

    // Export modal
    const [exportGeo, setExportGeo] = useState('');
    const [exportPassword, setExportPassword] = useState('');
    const [exportError, setExportError] = useState('');
    const [exporting, setExporting] = useState(false);

    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const raw: any = await dashboardApi.getLeadDashboard(startDate, endDate, geoFilter || undefined);
            // Lambda Function URL may return wrapped {statusCode, body} — unwrap if needed
            const res = raw?.body ? (typeof raw.body === 'string' ? JSON.parse(raw.body) : raw.body) : raw;
            setData(res);
        } catch (err: any) {
            setError(err.message || 'Failed to fetch lead dashboard');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, geoFilter]);

    const fetchRecent = useCallback(async (page = 1) => {
        try {
            setTableLoading(true);
            const filters = {
                geo: tableGeo || undefined,
                supplier: tableSupplier || undefined,
                status: tableStatus || undefined,
                start_date: startDate,
                end_date: endDate,
            };
            const raw: any = await dashboardApi.getLeadRecent(page, 50, filters);
            const res = raw?.body ? (typeof raw.body === 'string' ? JSON.parse(raw.body) : raw.body) : raw;
            setRecentData(res);
            setRecentPage(page);
        } catch { /* silent */ } finally {
            setTableLoading(false);
        }
    }, [startDate, endDate, tableGeo, tableSupplier, tableStatus]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchRecent(1); }, [fetchRecent]);

    const handleExport = async (geo: string) => {
        setExportGeo(geo);
        setExportPassword('');
        setExportError('');
    };

    const downloadSingleCsv = async (geo: string, password: string): Promise<boolean> => {
        const response = await dashboardApi.exportLeadCsv(geo, password);
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Export failed');
        }
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('text/csv')) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = response.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] || `subs_${geo}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            return true;
        }
        return false; // no CSV content (e.g. 0 pending)
    };

    const doExport = async () => {
        if (!exportPassword) { setExportError('Password required'); return; }
        setExporting(true);
        setExportError('');
        try {
            if (exportGeo === 'all') {
                // Download separate CSV per geo where pending > 50
                const qualifyingGeos = ['se', 'no', 'fi', 'de'].filter(g => (data?.queue_status[g] || 0) > 50);
                if (qualifyingGeos.length === 0) {
                    setExportError('No geos with > 50 pending leads');
                    setExporting(false);
                    return;
                }
                let downloaded = 0;
                for (const geo of qualifyingGeos) {
                    const ok = await downloadSingleCsv(geo, exportPassword);
                    if (ok) downloaded++;
                    // Small delay between downloads so browser doesn't block them
                    if (qualifyingGeos.indexOf(geo) < qualifyingGeos.length - 1) {
                        await new Promise(r => setTimeout(r, 500));
                    }
                }
                if (downloaded > 0) {
                    setExportGeo('');
                    fetchDashboard();
                    fetchRecent(recentPage);
                } else {
                    setExportError('No CSV data returned');
                }
            } else {
                const ok = await downloadSingleCsv(exportGeo, exportPassword);
                if (ok) {
                    setExportGeo('');
                    fetchDashboard();
                    fetchRecent(recentPage);
                } else {
                    setExportError('No pending leads');
                }
            }
        } catch (err: any) {
            setExportError(err.message || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    const kpis = data?.kpis || { total: 0, accepted: 0, rejected: 0, pending_sync: 0 };
    const acceptRate = kpis.total > 0 ? ((kpis.accepted / kpis.total) * 100).toFixed(1) : '0';

    // Grand totals for cost estimator
    const grandTotalCost = useMemo(() => {
        if (!data?.geo_breakdown) return { total: 0, cost: 0, eom_leads: 0, eom_cost: 0 };
        return data.geo_breakdown.reduce((acc, g) => ({
            total: acc.total + g.total,
            cost: acc.cost + g.total_cost,
            eom_leads: acc.eom_leads + g.eom_leads,
            eom_cost: acc.eom_cost + g.eom_cost,
        }), { total: 0, cost: 0, eom_leads: 0, eom_cost: 0 });
    }, [data]);

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in pb-12">
                {/* Top Tab Bar */}
                <div className="flex items-center gap-2 border-b border-gray-200 pb-3">
                    <button
                        onClick={() => { setActiveTab('intake'); navigate('/leads'); }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                            activeTab === 'intake'
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                    >
                        <Users size={16} />
                        <span>Supplier Intake & Costs</span>
                    </button>
                    <button
                        onClick={() => { setActiveTab('hygiene'); navigate('/leads/hygiene'); }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                            activeTab === 'hygiene'
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                    >
                        <ShieldCheck size={16} />
                        <span>DeBounce & Buffer Health</span>
                    </button>
                </div>

                {activeTab === 'hygiene' ? (
                    <LeadHygieneView />
                ) : (
                    <>
                        {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-indigo-600">
                            Lead Intake Dashboard
                        </h2>
                        <p className="text-gray-500 mt-1">Supplier pipeline overview &amp; cost estimator</p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white p-2 rounded-xl border border-gray-200 shadow-sm w-full md:w-auto">
                        {/* Period Preset Selector */}
                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                            {(Object.keys(PERIOD_LABELS) as PeriodPreset[]).map(preset => (
                                <button
                                    key={preset}
                                    onClick={() => handlePresetChange(preset)}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                                        periodPreset === preset
                                            ? 'bg-white text-indigo-700 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    {PERIOD_LABELS[preset]}
                                </button>
                            ))}
                        </div>

                        {/* Custom date inputs — only visible in custom mode */}
                        {periodPreset === 'custom' && (
                            <>
                                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                                    className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg p-2 focus:ring-indigo-500 focus:border-indigo-500" />
                                <span className="text-gray-400 hidden sm:block">→</span>
                                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                                    className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg p-2 focus:ring-indigo-500 focus:border-indigo-500" />
                            </>
                        )}

                        {/* Period label for non-custom modes */}
                        {periodPreset !== 'custom' && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 px-2">
                                <Calendar size={13} />
                                <span>{startDate} → {endDate}</span>
                            </div>
                        )}

                        <select value={geoFilter} onChange={e => setGeoFilter(e.target.value)}
                            className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg p-2.5 focus:ring-indigo-500 focus:border-indigo-500">
                            <option value="">All GEOs</option>
                            <option value="se">🇸🇪 Sweden</option>
                            <option value="no">🇳🇴 Norway</option>
                            <option value="fi">🇫🇮 Finland</option>
                            <option value="de">🇩🇪 Germany</option>
                        </select>
                    </div>
                </div>

                {error && <div className="bg-red-50 text-red-700 p-4 rounded-xl border border-red-200">{error}</div>}

                {/* Partner status banner */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 shadow-sm">
                    <div className="p-2 bg-amber-100 rounded-lg text-amber-700">
                        <AlertTriangle size={20} />
                    </div>
                    <div>
                        <h4 className="text-sm font-bold text-amber-900">Lead Campaigns Paused</h4>
                        <p className="text-xs text-amber-700 mt-1">
                            Lead campaigns for all suppliers (<strong>NordicLeads</strong>, LunaPark, eGentic, LeadOne) are temporarily closed on scheduled break. All incoming supplier API leads are being rejected with status <code className="bg-amber-100/50 px-1.5 py-0.5 rounded font-mono text-amber-900">campaigns_temporarily_closed</code>.
                        </p>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KpiCard title="Total Leads" value={kpis.total.toLocaleString('de-DE')} icon={<Users size={22} />} color="indigo" loading={loading} />
                    <KpiCard title="Accepted" value={`${kpis.accepted.toLocaleString('de-DE')}`} subtitle={`${acceptRate}%`} icon={<CheckCircle size={22} />} color="emerald" loading={loading} />
                    <KpiCard title="Rejected" value={kpis.rejected.toLocaleString('de-DE')} icon={<XCircle size={22} />} color="red" loading={loading} />
                    <KpiCard title="Pending Sync" value={kpis.pending_sync.toLocaleString('de-DE')} icon={<Clock size={22} />} color="amber" loading={loading} />
                </div>

                {/* Cost Estimator */}
                <div className="bg-white rounded-sm shadow-sm border border-gray-200 overflow-hidden">
                    <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                        <h3 className="text-sm font-bold text-gray-800 tracking-wide">
                            Cost Estimator — {data?.period ? `${new Date(data.period.start).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}` : '...'}
                        </h3>
                        {data?.period && (
                            <span className="text-xs text-gray-400">
                                Day {data.period.days_passed} of {data.period.days_in_month}
                            </span>
                        )}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs whitespace-nowrap">
                            <thead>
                                <tr className="bg-gray-50 text-gray-500 font-semibold border-b border-gray-100">
                                    <th className="px-6 py-3">GEO</th>
                                    <th className="px-4 py-3">Supplier</th>
                                    <th className="px-4 py-3 text-right">Leads</th>
                                    <th className="px-4 py-3 text-right">€/Lead</th>
                                    <th className="px-4 py-3 text-right">Cost</th>
                                    <th className="px-4 py-3 text-right">Est. EOM Leads</th>
                                    <th className="px-4 py-3 text-right">Est. EOM Cost</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {loading ? (
                                    <tr><td colSpan={7} className="px-6 py-8 text-center"><div className="animate-pulse h-4 bg-gray-200 rounded w-1/4 mx-auto" /></td></tr>
                                ) : (data?.geo_breakdown || []).map((geo) => {
                                    const suppliers = Object.entries(geo.suppliers);
                                    return suppliers.map(([sup, s], idx) => (
                                        <tr key={`${geo.geo}-${sup}`} className="hover:bg-gray-50">
                                            {idx === 0 && (
                                                <td className="px-6 py-2.5 font-bold text-gray-900" rowSpan={suppliers.length}>
                                                    {GEO_FLAGS[geo.geo]} {geo.geo.toUpperCase()}
                                                    {geo.max_cap && <span className="ml-1 text-xs text-amber-600 font-normal">(cap: {geo.max_cap.toLocaleString('de-DE')})</span>}
                                                </td>
                                            )}
                                            <td className="px-4 py-2.5">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                                    {SUPPLIER_LABELS[sup] || sup}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2.5 text-right font-medium">{s.count.toLocaleString('de-DE')}</td>
                                            <td className="px-4 py-2.5 text-right text-gray-500">€{s.price_per_lead.toFixed(2)}</td>
                                            <td className="px-4 py-2.5 text-right font-medium">€{s.cost.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                                            <td className="px-4 py-2.5 text-right text-gray-500">{s.eom_leads.toLocaleString('de-DE')}</td>
                                            <td className="px-4 py-2.5 text-right text-gray-500">€{s.eom_cost.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                                        </tr>
                                    ));
                                })}
                                {/* Geo Subtotals */}
                                {!loading && (data?.geo_breakdown || []).map((geo) => (
                                    <tr key={`${geo.geo}-total`} className="bg-gray-50 font-semibold text-gray-800">
                                        <td className="px-6 py-2">{GEO_FLAGS[geo.geo]} {geo.geo.toUpperCase()} Total</td>
                                        <td className="px-4 py-2"></td>
                                        <td className="px-4 py-2 text-right">{geo.total.toLocaleString('de-DE')}</td>
                                        <td className="px-4 py-2"></td>
                                        <td className="px-4 py-2 text-right">€{geo.total_cost.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                                        <td className="px-4 py-2 text-right">{geo.eom_leads.toLocaleString('de-DE')}</td>
                                        <td className="px-4 py-2 text-right">€{geo.eom_cost.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                ))}
                                {/* Grand Total */}
                                {!loading && (
                                    <tr className="bg-indigo-50 font-bold text-indigo-900 border-t-2 border-indigo-200">
                                        <td className="px-6 py-3" colSpan={2}>Grand Total</td>
                                        <td className="px-4 py-3 text-right">{grandTotalCost.total.toLocaleString('de-DE')}</td>
                                        <td className="px-4 py-3"></td>
                                        <td className="px-4 py-3 text-right">€{grandTotalCost.cost.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                                        <td className="px-4 py-3 text-right">{grandTotalCost.eom_leads.toLocaleString('de-DE')}</td>
                                        <td className="px-4 py-3 text-right">€{grandTotalCost.eom_cost.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Queue Status + Export */}
                <div className="bg-white rounded-sm shadow-sm border border-gray-200 overflow-hidden">
                    <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                        <h3 className="text-sm font-bold text-gray-800 tracking-wide">Feedblitz Import Queue</h3>
                        <button
                            disabled={kpis.pending_sync === 0}
                            onClick={() => handleExport('all')}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition ${kpis.pending_sync > 0
                                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                        >
                            <Download size={14} /> Download All ({kpis.pending_sync.toLocaleString('de-DE')})
                        </button>
                    </div>
                    <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {['se', 'no', 'fi', 'de'].map(geo => {
                            const count = data?.queue_status[geo] || 0;
                            return (
                                <div key={geo} className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div>
                                        <div className="text-sm font-bold text-gray-800">{GEO_FLAGS[geo]} {geo.toUpperCase()}</div>
                                        <div className={`text-2xl font-bold mt-1 ${count > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                                            {count.toLocaleString('de-DE')}
                                        </div>
                                        <div className="text-xs text-gray-400">pending</div>
                                    </div>
                                    <button
                                        disabled={count === 0}
                                        onClick={() => handleExport(geo)}
                                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition ${count > 0
                                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                                    >
                                        <Download size={14} /> CSV
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Export Password Modal */}
                {exportGeo && (
                    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setExportGeo('')}>
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-indigo-100 rounded-lg"><Lock size={20} className="text-indigo-600" /></div>
                                <div>
                                    <h3 className="font-bold text-gray-900">Export {exportGeo === 'all' ? 'All GEOs' : exportGeo.toUpperCase()} Leads</h3>
                                    <p className="text-xs text-gray-500">Password required to download unmasked data</p>
                                </div>
                            </div>
                            <input
                                type="password"
                                placeholder="Enter export password..."
                                value={exportPassword}
                                onChange={e => setExportPassword(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && doExport()}
                                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-indigo-500 focus:border-indigo-500 mb-3"
                                autoFocus
                            />
                            {exportError && <p className="text-red-600 text-xs mb-3">{exportError}</p>}
                            <div className="flex gap-2">
                                <button onClick={() => setExportGeo('')} className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">Cancel</button>
                                <button onClick={doExport} disabled={exporting}
                                    className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50">
                                    {exporting ? 'Exporting...' : 'Download'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Recent Leads Table */}
                <div className="bg-white rounded-sm shadow-sm border border-gray-200 overflow-hidden">
                    <div className="border-b border-gray-100 px-6 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <h3 className="text-sm font-bold text-gray-800 tracking-wide">Latest Leads</h3>
                        <div className="flex items-center gap-2 flex-wrap">
                            <Filter size={14} className="text-gray-400" />
                            <select value={tableGeo} onChange={e => setTableGeo(e.target.value)}
                                className="bg-gray-50 border border-gray-200 text-gray-700 text-xs rounded-lg p-1.5">
                                <option value="">All GEOs</option>
                                <option value="se">SE</option><option value="no">NO</option>
                                <option value="fi">FI</option><option value="de">DE</option>
                            </select>
                            <select value={tableSupplier} onChange={e => setTableSupplier(e.target.value)}
                                className="bg-gray-50 border border-gray-200 text-gray-700 text-xs rounded-lg p-1.5">
                                <option value="">All Suppliers</option>
                                <option value="nl">NL</option><option value="lp">LP</option>
                                <option value="eg">EG</option><option value="lo">LO</option>
                            </select>
                            <select value={tableStatus} onChange={e => setTableStatus(e.target.value)}
                                className="bg-gray-50 border border-gray-200 text-gray-700 text-xs rounded-lg p-1.5">
                                <option value="">All Status</option>
                                <option value="accepted">Accepted</option>
                                <option value="rejected">Rejected</option>
                            </select>
                            {recentData && (
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                    <span>{recentData.total.toLocaleString('de-DE')} total</span>
                                    <span>|</span>
                                    <span>Page {recentData.page}/{recentData.total_pages}</span>
                                    <button disabled={recentData.page <= 1} onClick={() => fetchRecent(recentData.page - 1)}
                                        className="p-0.5 rounded bg-gray-100 disabled:opacity-50"><ChevronLeft size={14} /></button>
                                    <button disabled={recentData.page >= recentData.total_pages} onClick={() => fetchRecent(recentData.page + 1)}
                                        className="p-0.5 rounded bg-gray-100 disabled:opacity-50"><ChevronRight size={14} /></button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[700px] text-left text-xs whitespace-nowrap">
                            <thead>
                                <tr className="bg-gray-50 text-gray-500 font-semibold border-b border-gray-100">
                                    <th className="px-6 py-3">ID</th>
                                    <th className="px-4 py-3">Email</th>
                                    <th className="px-4 py-3">Name</th>
                                    <th className="px-4 py-3">GEO</th>
                                    <th className="px-4 py-3">Supplier</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Reason</th>
                                    <th className="px-4 py-3">Timestamp</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {tableLoading ? (
                                    <tr><td colSpan={8} className="px-6 py-8 text-center"><div className="animate-pulse h-4 bg-gray-200 rounded w-1/4 mx-auto" /></td></tr>
                                ) : (recentData?.leads || data?.recent_leads || []).map((lead) => (
                                    <tr key={lead.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-2.5 font-mono text-indigo-600 font-medium">{lead.id.toLocaleString('de-DE')}</td>
                                        <td className="px-4 py-2.5 text-gray-500">{lead.email_masked}</td>
                                        <td className="px-4 py-2.5">{lead.first_name}</td>
                                        <td className="px-4 py-2.5 font-bold">{GEO_FLAGS[lead.geo] || ''} {lead.geo.toUpperCase()}</td>
                                        <td className="px-4 py-2.5">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                                {lead.supplier.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[lead.lead_status] || 'bg-gray-100 text-gray-800'}`}>
                                                {lead.lead_status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-gray-400 max-w-[200px] truncate" title={lead.reject_reason}>{lead.reject_reason || '-'}</td>
                                        <td className="px-4 py-2.5 text-gray-400">{lead.original_timestamp ? new Date(lead.original_timestamp).toLocaleString('sv-SE', { timeZone: 'Europe/Copenhagen' }) : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Reject Reasons */}
                {data?.reject_reasons && Object.keys(data.reject_reasons).length > 0 && (
                    <div className="bg-white rounded-sm shadow-sm border border-gray-200 overflow-hidden">
                        <div className="border-b border-gray-100 px-6 py-4">
                            <h3 className="text-sm font-bold text-gray-800 tracking-wide">Rejection Reasons</h3>
                        </div>
                        <div className="p-6 flex flex-wrap gap-3">
                            {Object.entries(data.reject_reasons).sort(([, a], [, b]) => b - a).map(([reason, count]) => (
                                <div key={reason} className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 flex items-center gap-3">
                                    <span className="text-xs text-red-700 font-medium">{reason}</span>
                                    <span className="text-sm font-bold text-red-900">{count.toLocaleString('de-DE')}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                    </>
                )}
            </div>
        </MainLayout>
    );
}

function KpiCard({ title, value, subtitle, icon, color, loading }: {
    title: string; value: string; subtitle?: string; icon: React.ReactNode; color: string; loading: boolean;
}) {
    const colorMap: Record<string, string> = {
        indigo: 'bg-indigo-50 text-indigo-600',
        emerald: 'bg-emerald-50 text-emerald-600',
        red: 'bg-red-50 text-red-600',
        amber: 'bg-amber-50 text-amber-600',
    };
    return (
        <div className="bg-white rounded-sm shadow-sm border border-gray-200 p-5 flex items-start justify-between hover:shadow-md transition-shadow">
            <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{title}</p>
                <h3 className="text-2xl font-bold text-gray-900 tracking-tight font-display">
                    {loading ? <div className="h-7 w-20 bg-gray-200 rounded animate-pulse" /> : value}
                </h3>
                {subtitle && !loading && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
            </div>
            <div className={`p-2.5 rounded-lg ${colorMap[color] || colorMap.indigo}`}>
                {icon}
            </div>
        </div>
    );
}
